import { Location } from "@angular/common";
import { Injectable, Injector } from "@angular/core";
import { FormArray, FormBuilder, FormControl, FormGroup } from "@angular/forms";
import { ActivatedRoute, Params, Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { Subject, Subscription } from "rxjs";
import { UtilHelper } from "../helpers/util.helper";
import { Base } from "../models/base.model";
import { Company, CompanyModules } from "../models/company.model";
import { Contact } from "../models/contact.model";
import { AttendanceStatusEnum } from "../models/enum/attendance.status.enum";
import { ChannelTypeEnum } from "../models/enum/channel.type";
import { RoleUserEnum } from "../models/enum/role.user.enum";
import { User } from "../models/user.model";
import { ListStateCacheService } from "../services/cache/list-state-cache.service";
import { CacheKeyEnum, ListState } from "../services/cache/list-state-cache.types";
import { AlertService } from "../shared/components/alert/alert.service";
import { SearchParams } from "./abstract.types";
import { CONSTANTS, Constants, ENV, Environment } from "./injection.token";
import { Pager } from "./pager";

@Injectable()
export abstract class AbstractComponent {

  public destroy$ = new Subject<void>();

  protected location: Location;
  protected router: Router;
  protected route: ActivatedRoute;
  protected formBuilder: FormBuilder;
  protected alertService: AlertService;
  protected constants: Constants;
  protected environment: Environment;
  protected subscriptions: Subscription = new Subscription();
  protected listCache: ListStateCacheService;
  protected translateService: TranslateService;

  public cacheKey: CacheKeyEnum;

  public searchString: string = '';

  public searchParams: any;
  public formGroup: FormGroup;
  public loading: boolean = false;
  public loadingContent: boolean = false;
  public isNew: boolean = false;

  public emailRegex: RegExp = /^[a-z0-9!#$%&'*+\/=?^_`{|}~.-]+@[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)*$/i;
  public domainRegex: RegExp = /^([a-z0-9|-]+[a-z0-9]{1,}\.)*[a-z0-9|-]+[a-z0-9]{1,}\.[a-z]{2,}$/i;
  public dateRegex: RegExp = /^(?:(?:31(\/|-|\.)(?:0?[13578]|1[02]))\1|(?:(?:29|30)(\/|-|\.)(?:0?[13-9]|1[0-2])\2))(?:(?:1[6-9]|[2-9]\d)?\d{2})$|^(?:29(\/|-|\.)0?2\3(?:(?:(?:1[6-9]|[2-9]\d)?(?:0[48]|[2468][048]|[13579][26])|(?:(?:16|[2468][048]|[3579][26])00))))$|^(?:0?[1-9]|1\d|2[0-8])(\/|-|\.)(?:(?:0?[1-9])|(?:1[0-2]))\4(?:(?:1[6-9]|[2-9]\d)?\d{2})$/i;
  public timeRegex: RegExp = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/i;
  public phoneNumberRegex: RegExp = /^-?(0|[1-9]\d*)?$/;
  public domainHttpRegex: RegExp = /^(https?:\/\/)?([a-zA-Z0-9.-]+)(\.[a-zA-Z]{2,})(:[0-9]{1,5})?(\/[^\s]*)?$/i;

  public templateVariableList = [
    { value: '{{SAUDACAO}}', label: 'Saudação', icon: 'pan_tool' },
    { value: '{{NOME}}', label: 'Nome do Lead', icon: 'person' },
    { value: '{{EMPRESA}}', label: 'Empresa', icon: 'business' },
    { value: '{{PROTOCOLO}}', label: 'Número do Protocolo', icon: 'description' },
    { value: '{{LINK_CHANNEL}}', label: 'Link canal Whatsapp', icon: 'insert_link' }
  ];

  public templateVariableResponseAutomatedList = [
    { value: '{{SAUDACAO}}', label: 'Saudação', icon: 'pan_tool' },
    { value: '{{NOME}}', label: 'Nome do Lead', icon: 'person' },
    { value: '{{EMPRESA}}', label: 'Empresa', icon: 'business' },
    { value: '{{PROTOCOLO}}', label: 'Número do Protocolo', icon: 'description' },
    { value: '{{LINK_CHANNEL}}', label: 'Link canal Whatsapp', icon: 'insert_link' },
    { value: '{{ATENDENTE_ATUAL}}', label: 'Nome do atendente atual', icon: 'perm_identity' },
    { value: '{{ATENDENTE_NOVO}}', label: 'Nome do novo atendente', icon: 'perm_identity' }
  ];

  // V2
  public templateVariableListWithNameAttendant = [
    { value: '{{SAUDACAO}}', label: 'Saudação', icon: 'mdi-allergy' },
    { value: '{{NOME}}', label: 'Nome do Lead', icon: 'person' },
    { value: '{{EMPRESA}}', label: 'Empresa', icon: 'business' },
    { value: '{{PROTOCOLO}}', label: 'Número do Protocolo', icon: 'description' },
    { value: '{{LINK_CHANNEL}}', label: 'Link canal Whatsapp', icon: 'insert_link' },
    { value: '{{NOME_ATENDENTE}}', label: 'Nome do atendente', icon: 'perm_identity' }
  ];

  constructor(injector: Injector) {
    this.location = injector.get(Location);
    this.router = injector.get(Router);
    this.route = injector.get(ActivatedRoute);
    this.alertService = injector.get(AlertService);
    this.formBuilder = injector.get(FormBuilder);
    this.constants = injector.get(CONSTANTS);
    this.environment = injector.get(ENV);
    this.listCache = injector.get(ListStateCacheService);
    this.translateService = injector.get(TranslateService);
  }

  /** i18n helper for TS code (toastr/alerts/confirm). Resolves a key synchronously. */
  protected t(key: string, params?: object): string {
    return this.translateService.instant(key, params);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  public get env() {
    return this.environment['env'];
  }

  getIDCurrentUser(): string {
    return this.getCurrentUserUser()._id;
  }

  getRoles(): Array<string> {
    return this.getCurrentUserUser().roles;
  }

  getNameCurrentUser(): string {
    return this.getCurrentUserUser().name;
  }

  getEmailCurrentUser(): string {
    return this.getCurrentUserUser().email;
  }

  getIDCurrentCompany(): string {
    return this.getCurrentUserUser().company._id;
  }

  getCurrentUserUser(): User {
    return JSON.parse(localStorage.getItem(this.constants['currentUser'])).user;
  }

  getIDDepartmentsUserLogged(): string[] {
    return this.getCurrentUserUser()?.['departments'];
  }

  getCompanyCurrentUser(): Company {
    return this.getCurrentUserUser().company;
  }

  getNameCompanyCurrentUser(): string {
    return this.getCurrentUserUser().company.name;
  }

  getCodeCompanyCurrentUser(): string {
    return this.getCurrentUserUser().company.code;
  }

  private getModules(): CompanyModules {
    return this.getCurrentUserUser().company.modules;
  }

  public getModuleCampaign(): boolean {
    return this.getModules().moduleCampaign ?? false;
  }

  public getModuleCSAT(): boolean {
    return this.getModules().moduleCSAT ?? false;
  }

  public getModuleTrendVoip(): boolean {
    return this.getModules().moduleTrendVoip ?? false;
  }

  public getModuleCustomerPortfolio(): boolean {
    return this.getModules().moduleCustomerPortfolio ?? false;
  }

  public getModuleTracking(): boolean {
    return this.getModules().moduleTracking ?? false;
  }

  public getModuleWebhook(): boolean {
    return this.getModules().moduleWebhook ?? false;
  }

  public getModuleWebhookSantri(): boolean {
    return this.getModules().moduleWebhookSantri ?? false;
  }

  public getModulePipedrive(): boolean {
    return this.getModules().modulePipedrive ?? false;
  }

  public getModuleAgendor(): boolean {
    return this.getModules().moduleAgendor ?? false;
  }

  public getModuleOpenAI(): boolean {
    return this.getModules().moduleOpenAI ?? false;
  }

  public getModuleTrendGPT(): boolean {
    return this.getModules().moduleTrendGPT ?? false;
  }

  public getModuleCRM(): boolean {
    return this.getModules().moduleCRM ?? false;
  }

  public getModuleAPI(): boolean {
    return this.getModules().moduleAPI ?? false;
  }


  public getModuleCopilot(): boolean {
    return this.getModules().moduleCopilot ?? false;
  }

  public getModuleFlow(): boolean {
    return this.getModules().moduleFlow ?? false;
  }

  public getModuleChat(): boolean {
    return this.getModules().moduleChat ?? false;
  }

  public getModuleIntegrations(): boolean {
    return this.getModuleWebhook()
      || this.getModuleWebhookSantri()
      || this.getModulePipedrive()
      || this.getModuleAgendor()
      || this.getModuleOpenAI()
      || this.getModuleTrendGPT();
  }

  getTimezoneCompanyCurrentUser(): string {
    return this.getCurrentUserUser().company.timezone ?? 'America/Sao_Paulo';
  }

  getSignatureConversation(): boolean {
    return this.getCurrentUserUser().signatureConversation ?? false;
  }

  getCurrentUser(): any {
    return JSON.parse(localStorage.getItem(this.constants['currentUser']));
  }

  getClientVisibilityCurrentUser(): boolean {
    if (this.isAttendant()) {
      return !this.getCurrentUserUser()?.clientVisibilitys;
    }
    return true;
  }

  getMaskedPhone(value: string): string {
    if (this.getClientVisibilityCurrentUser()) {
      return value;
    } else {
      if (!value || value.length <= 4) return value;
      const visible = value.slice(-4);
      const masked = '*'.repeat(value.length - 4);
      return masked + visible;
    }
  }

  getAddLeadsCurrentUser(): boolean {
    if (this.isAttendant()) {
      return this.getCurrentUserUser()?.addLeads;
    }
    return true;
  }

  /**
   * Whether the user may apply or lift the CRM blacklist — on the customer list and through
   * a blacklist discard (E3 RF-22). Mirrors `CustomerBusinessService.assertCanBlacklist`:
   * ADMIN/SUPERVISOR, or the `blockContact` permission.
   */
  canBlacklist(): boolean {
    return this.isAdmin() || this.isSupervisor() || this.getCurrentUserUser()?.blockContact === true;
  }

  getParam(key: string): any {
    let result = null;
    this.route.params.forEach((params: Params) => {
      result = params[key];
    });
    return result;
  }

  getQueryParam(key: string) {
    let result = null;
    this.route.queryParamMap.subscribe(queryParams => {
      result = queryParams.get(key);
    });
    return result;
  }

  isRoot() {
    const user = this.getCurrentUserUser();
    if (user) {
      return user.root ?? false;
    }
    return false;
  }

  isAdmin() {
    const roles = this.getRoles();
    if (!!roles && roles.length > 0) {
      return roles.length === 3
        && roles.includes(RoleUserEnum.ADMIN)
        && roles.includes(RoleUserEnum.SUPERVISOR)
        && roles.includes(RoleUserEnum.ATTENDANT);
    }
    return false;
  }

  isSubscriptionActive() {
    const user = this.getCurrentUserUser();
    if (!!user && user.company.subscription) {
      return true;
    }
    return false;
  }

  isSupervisor(): boolean {
    const roles = this.getRoles();
    if (!!roles && roles.length > 0) {
      return roles.length === 2
        && roles.includes(RoleUserEnum.SUPERVISOR)
        && roles.includes(RoleUserEnum.ATTENDANT);
    }
    return false;
  }

  /**
   * Whether this user sees every deal of the company
   * (SPEC-2026-08-crm-pipeline-deals-radar — RF-22, RF-26).
   *
   * Client-side MIRROR of the API's own `canViewAllDeals`, and it exists only to shape
   * the interface — to stop offering a filter that can only come back empty. The rule
   * is enforced server-side on every card read (ADR-003); nothing here grants access.
   *
   * `viewAllDeals` is honoured only when it is explicitly `false`. The API treats an
   * absent flag as restrictive, but here an absent flag means the session payload
   * simply did not carry it — and locking a filter for a user the API would answer
   * unrestricted would be a regression, not a safeguard.
   */
  canViewAllDeals(): boolean {
    if (this.isRoot() || this.isAdmin() || this.isSupervisor()) return true;

    return this.getCurrentUserUser()?.viewAllDeals !== false;
  }

  isAttendant(): boolean {
    const roles = this.getRoles();
    if (!!roles && roles.length > 0) {
      return roles.length === 1
        && roles.includes(RoleUserEnum.ATTENDANT);
    }
    return false;
  }

  goBack(): void {
    this.location.back();
  }

  reset() {
    this.formGroup.reset();
  }

  isFieldValid(field: string, formGroup?: FormGroup) {
    if (!formGroup) {
      return !this.formGroup.get(field).valid && this.formGroup.get(field).touched;
    } else {
      return !formGroup.get(field).valid && formGroup.get(field).touched;
    }
  }

  displayFieldCss(field: string, formGroup?: FormGroup) {
    return {
      'is-invalid': (formGroup ? this.isFieldValid(field, formGroup) : this.isFieldValid(field)),
      // 'is-valid': (formGroup ? this.isFieldValid(field, formGroup) : this.isFieldValid(field))
    };
  }

  isFieldInvalidAndDirtyAndTouched(field: string, formGroup?: FormGroup) {
    if (!formGroup) {
      return this.formGroup.get(field).invalid && (this.formGroup.get(field).dirty || this.formGroup.get(field).touched);
    } else {
      return formGroup.get(field).invalid && (formGroup.get(field).dirty || formGroup.get(field).touched);
    }
  }

  isFieldErrosRequired(field: string, formGroup?: FormGroup) {
    if (!formGroup) {
      return this.formGroup.get(field).errors['required'];
    } else {
      return formGroup.get(field).errors['required'];
    }
  }

  isFieldErrosPattern(field: string, formGroup?: FormGroup) {
    if (!formGroup) {
      return this.formGroup.get(field).errors['pattern'];
    } else {
      return formGroup.get(field).errors['pattern'];
    }
  }

  validateAllFormFields(formGroup: FormGroup) {
    Object.keys(formGroup.controls).forEach(field => {
      const control = formGroup.get(field);
      if (control instanceof FormControl) {
        control.markAsTouched({ onlySelf: true });
      } else if (control instanceof FormGroup) {
        this.validateAllFormFields(control);
      } else if (control instanceof FormArray) {
        control.controls.forEach(formGroup => {
          Object.keys(formGroup.value).forEach(field => {
            const control = formGroup.get(field);
            if (control instanceof FormControl) {
              control.markAsTouched({ onlySelf: true });
            } else if (control instanceof FormGroup) {
              this.validateAllFormFields(control);
            }
          });
        });
      }
    });
  }

  public getUserImage(user: User = this.getCurrentUserUser()) {
    return UtilHelper.getUserImage(user);
  }

  public getContactImage(contact: Contact) {
    return UtilHelper.getContactImage(contact);
  }

  public onImageError(event: Event): void {
    return UtilHelper.onImageError(event);
  }

  public getCompanyImage(company: Company) {
    return UtilHelper.getCompanyImage(company);
  }

  public getTranslatedTypeChannel(type: ChannelTypeEnum) {
    const types = {
      [ChannelTypeEnum.CLOUD_API]: 'Cloud API',
      [ChannelTypeEnum.Z_API]: 'TrendBusiness',
      [ChannelTypeEnum.INSTAGRAM_API]: 'Instagram',
      [ChannelTypeEnum.MESSENGER_API]: 'Messenger',
      [ChannelTypeEnum.RCS_API]: 'RCS',
      [ChannelTypeEnum.VOIP_API]: 'VoIP',
      [ChannelTypeEnum.WEBCHAT_API]: 'Webchat',
    };
    return types[type];
  }

  public getTranslatedStatusAttendance(status: AttendanceStatusEnum) {
    const statuses = {
      [AttendanceStatusEnum.OVERTIME]: 'attendance.status.overtime',
      [AttendanceStatusEnum.PENDING]: 'attendance.status.pending',
      [AttendanceStatusEnum.IN_PROGRESS]: 'attendance.status.inProgress',
      [AttendanceStatusEnum.PAUSED]: 'attendance.status.paused',
      [AttendanceStatusEnum.CLOSED]: 'attendance.status.closed',
    };
    return this.t(statuses[status]);
  }


  public compareWithObjectBase(value1: Base, value2: Base): boolean {
    return value1._id === value2._id;
  }

  protected restoreState<T>(): ListState<T> | null {
    return this.listCache.get<T>(this.cacheKey);
  }

  protected saveState<T>(state: ListState<T>): void {
    this.listCache.set<T>(this.cacheKey, state);
  }

  protected persistListState<T>(pager: Pager<T>, search: string, params: Partial<SearchParams>): void {
    const state: ListState<T> = { pager, search, params }
    this.saveState<T>(state);
  }

  protected clearState(): void {
    this.listCache.clear(this.cacheKey);
  }

}
